import { Box, Grid, List, ListItem, ListItemIcon, ListItemText, Typography, useMediaQuery } from "@mui/material";
import { theme } from "../../common/style/Theme";
import { DoubleArrow } from "@mui/icons-material";

const Cookies = () => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));

    const sections = [
        {
            title: "1. What Are Cookies",
            paragraphs: [
                "Cookies are small text files that are stored on your computer, smartphone, or other device when you visit a website.",
                "Cookies help websites function efficiently and provide information to website owners about how visitors interact with the website.",
            ],
        },
        {
            title: "2. How We Use Cookies",
            content: "M5 Info Solutions Private Limited uses cookies for the following purposes:",
            subsections: [
                {
                    heading: "Website Functionality",
                    content: "Cookies help ensure that the website functions properly and improves user experience.",
                },
                {
                    heading: "Website Analytics",
                    content: "We may use cookies to understand how visitors interact with our website, including:",
                    bullets: [
                        "Pages visited",
                        "Time spent on pages",
                        "Navigation patterns",
                    ],
                    footer: "This helps us improve website performance and content.",
                },
                {
                    heading: "Security",
                    content: "Cookies may be used to help maintain the security of the website and prevent unauthorized access.",
                },
                {
                    heading: "User Preferences",
                    content: "Cookies may remember certain preferences such as language settings or previously entered information in forms.",
                },
            ],
        },
        {
            title: "3. Types of Cookies We Use",
            subsections: [
                {
                    heading: "Essential Cookies",
                    content: "These cookies are necessary for the website to function properly and cannot be disabled in our systems.",
                    afterContent: "Examples include:",
                    bullets: [
                        "Page navigation",
                        "Access to secure areas",
                        "Website stability",
                    ],
                },
                {
                    heading: "Analytics Cookies",
                    content: "These cookies help us understand how visitors use the website so we can improve functionality and performance. Examples include analytics tools such as website traffic measurement.",
                },
                {
                    heading: "Performance Cookies",
                    content: "These cookies collect information about how visitors use the website to help improve speed and usability.",
                },
                {
                    heading: "Functional Cookies",
                    content: "These cookies allow the website to remember choices you make such as preferences or previously viewed content.",
                },
            ],
        },
        {
            title: "4. Third-Party Cookies",
            content: "Some cookies may be placed by third-party services integrated with our website, such as:",
            bullets: [
                "Website analytics tools",
                "Embedded content",
                "Digital marketing tools",
            ],
            footer: "These third parties may collect information in accordance with their own privacy policies. M5 Info Solutions Private Limited does not control how these third parties use cookies.",
        },
        {
            title: "5. Managing Cookies",
            content: "Most web browsers allow you to control or disable cookies through browser settings.",
            afterContent: "You may choose to:",
            bullets: [
                "Delete existing cookies",
                "Block certain types of cookies",
                "Receive notifications before cookies are stored",
            ],
            footer: "Please note that disabling cookies may affect the functionality of some parts of the website.",
        },
        {
            title: "6. Data Collected Through Cookies",
            content: "Information collected through cookies may include:",
            bullets: [
                "IP address",
                "Browser type",
                "Device information",
                "Pages visited",
                "Website activity",
            ],
            footer: "This information is generally used in an aggregated and non-identifiable form to analyze website performance.",
        },
        {
            title: "7. Updates to This Cookie Policy",
            content: "We may update this Cookie Policy from time to time to reflect changes in technology, legal requirements, or website functionality. Updated versions will be published on this page with the revised effective date.",
        },
        {
            title: "8. Contact Information",
            content: "If you have any questions about this Cookie Policy, please contact:",
            subContent: "M5 Info Solutions Private Limited\nBengaluru, Karnataka\nIndia",
            afterContent: "Email: info@m5infosolutions.com\nWebsite: www.m5infosolutions.com",
        },
    ];

    return (
        <Box>
            {/* Hero Section */}
            <Box style={{
                width: "100vw",
                height: isMobile ? "50vh" : "45vh",
                position: "relative",
                background: `linear-gradient(135deg, ${theme.palette.common.black}, ${theme.palette.primary.main})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}>
                <Box style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `repeating-linear-gradient(
                        45deg,
                        transparent,
                        transparent 2px,
                        rgba(255,255,255,0.03) 2px,
                        rgba(255,255,255,0.03) 4px
                    )`,
                    zIndex: 0,
                }} />
                <Box style={{
                    position: "relative",
                    zIndex: 1,
                    textAlign: "center",
                    padding: isMobile ? "0 5vw" : "0 10vw",
                }}>
                    <Typography variant={isMobile ? "h3" : "h1"} style={{
                        color: theme.palette.common.white,
                        fontWeight: 800,
                        marginBottom: "15px",
                        fontSize: isMobile ? "2rem" : isTablet ? "2.8rem" : "3.8rem",
                    }}>
                        Cookie Policy
                    </Typography>
                    <Typography variant="body1" style={{
                        color: theme.palette.common.white,
                        opacity: 0.8,
                        maxWidth: "700px",
                        margin: "0 auto",
                        lineHeight: "1.7",
                    }}>
                        Learn how we use cookies and similar technologies to improve your experience.
                    </Typography>
                </Box>
            </Box>

            {/* Effective Date & Intro */}
            <Grid container style={{
                padding: isMobile ? "8vw 5vw 4vw 5vw" : isTablet ? "5vw 8vw 3vw 8vw" : "4vw 15vw 2vw 15vw",
            }}>
                <Grid size={{ xs: 12 }}>
                    <Box style={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        padding: isMobile ? "20px 25px" : "25px 35px",
                        borderRadius: "12px",
                        marginBottom: "30px",
                    }}>
                        <Typography variant="h6" style={{
                            color: theme.palette.common.white,
                            fontWeight: 600,
                        }}>
                            Effective Date: January 1, 2026
                        </Typography>
                    </Box>
                    <Typography variant="body1" style={{
                        lineHeight: "1.8",
                        marginBottom: "15px",
                    }}>
                        This Cookie Policy explains how M5 Info Solutions Private Limited ("M5", "Company", "we", "our", or "us") uses cookies and similar technologies when you visit www.m5infosolutions.com.
                    </Typography>
                    <Typography variant="body1" style={{
                        lineHeight: "1.8",
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                    }}>
                        By continuing to browse or use our website, you agree to our use of cookies as described in this policy.
                    </Typography>
                </Grid>
            </Grid>

            {/* Sections */}
            <Grid container style={{
                padding: isMobile ? "0 5vw 8vw 5vw" : isTablet ? "0 8vw 5vw 8vw" : "0 15vw 5vw 15vw",
            }}>
                <Grid size={{ xs: 12 }}>
                    {sections.map((section, idx) => (
                        <Box key={idx} style={{
                            marginBottom: "35px",
                            padding: isMobile ? "25px 20px" : "30px 35px",
                            background: idx % 2 === 0 ? theme.palette.background.paper : theme.palette.common.white,
                            borderRadius: "12px",
                            borderLeft: `4px solid ${idx % 2 === 0 ? theme.palette.primary.main : theme.palette.secondary.main}`,
                            boxShadow: "rgba(17, 12, 46, 0.08) 0px 8px 40px 0px",
                        }}>
                            <Typography variant="h5" style={{
                                fontWeight: 700,
                                color: theme.palette.primary.main,
                                marginBottom: "15px",
                                fontSize: isMobile ? "1.15rem" : "1.25rem",
                            }}>
                                {section.title}
                            </Typography>

                            {section.paragraphs && section.paragraphs.map((p, pIdx) => (
                                <Typography key={pIdx} variant="body1" style={{
                                    lineHeight: "1.8",
                                    marginBottom: "12px",
                                }}>
                                    {p}
                                </Typography>
                            ))}

                            {section.content && (
                                <Typography variant="body1" style={{
                                    lineHeight: "1.8",
                                    marginBottom: "12px",
                                }}>
                                    {section.content}
                                </Typography>
                            )}

                            {section.subContent && (
                                <Box style={{
                                    background: `${theme.palette.primary.light}30`,
                                    padding: "15px 20px",
                                    borderRadius: "8px",
                                    marginBottom: "15px",
                                }}>
                                    <Typography variant="body1" style={{
                                        fontWeight: 600,
                                        whiteSpace: "pre-line",
                                        lineHeight: "1.8",
                                    }}>
                                        {section.subContent}
                                    </Typography>
                                </Box>
                            )}

                            {section.afterContent && (
                                <Typography variant="body1" style={{
                                    lineHeight: "1.8",
                                    marginBottom: "8px",
                                    whiteSpace: "pre-line",
                                }}>
                                    {section.afterContent}
                                </Typography>
                            )}

                            {section.subsections && section.subsections.map((sub, sIdx) => (
                                <Box key={sIdx} style={{
                                    marginBottom: "20px",
                                    marginLeft: isMobile ? "0" : "10px",
                                    paddingLeft: isMobile ? "10px" : "20px",
                                    borderLeft: `2px solid ${theme.palette.primary.light}`,
                                }}>
                                    <Typography variant="h6" style={{
                                        fontWeight: 600,
                                        color: theme.palette.secondary.main,
                                        marginBottom: "8px",
                                        fontSize: isMobile ? "1rem" : "1.1rem",
                                    }}>
                                        {sub.heading}
                                    </Typography>
                                    <Typography variant="body1" style={{
                                        lineHeight: "1.8",
                                        marginBottom: "8px",
                                    }}>
                                        {sub.content}
                                    </Typography>
                                    {sub.afterContent && (
                                        <Typography variant="body1" style={{
                                            lineHeight: "1.8",
                                            marginBottom: "8px",
                                        }}>
                                            {sub.afterContent}
                                        </Typography>
                                    )}
                                    {sub.bullets && (
                                        <List style={{ padding: "0 0 0 5px" }}>
                                            {sub.bullets.map((bullet, bIdx) => (
                                                <ListItem key={bIdx} style={{ padding: "3px 0" }}>
                                                    <ListItemIcon style={{ minWidth: "35px" }}>
                                                        <DoubleArrow style={{
                                                            color: theme.palette.secondary.main,
                                                            fontSize: "1.1rem",
                                                        }} />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={bullet}
                                                        primaryTypographyProps={{
                                                            style: { lineHeight: "1.7" }
                                                        }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    )}
                                    {sub.footer && (
                                        <Typography variant="body1" style={{
                                            lineHeight: "1.8",
                                            marginTop: "5px",
                                        }}>
                                            {sub.footer}
                                        </Typography>
                                    )}
                                </Box>
                            ))}

                            {section.bullets && (
                                <List style={{ padding: "0 0 0 5px" }}>
                                    {section.bullets.map((bullet, bIdx) => (
                                        <ListItem key={bIdx} style={{ padding: "3px 0" }}>
                                            <ListItemIcon style={{ minWidth: "35px" }}>
                                                <DoubleArrow style={{
                                                    color: theme.palette.secondary.main,
                                                    fontSize: "1.1rem",
                                                }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={bullet}
                                                primaryTypographyProps={{
                                                    style: { lineHeight: "1.7" }
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}

                            {section.footer && (
                                <Typography variant="body1" style={{
                                    lineHeight: "1.8",
                                    marginTop: "10px",
                                    fontWeight: 500,
                                    color: theme.palette.text.primary,
                                }}>
                                    {section.footer}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Grid>
            </Grid>
        </Box>
    );
};

export default Cookies;
